<script setup lang="ts">
import LeButton from "@dsk/components/Button/LeButton.vue";
import LeHeading from "@dsk/components/Heading/LeHeading.vue";
import LeLabel from "@dsk/components/Label/LeLabel.vue";
import type { Label, Link } from "@dsk/types";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import defaultImageUrl from "./images/card-default-image@1x.png";
import defaultHighDpiImageUrl from "./images/card-default-image@2x.png";

interface Props {
  headline: string;
  headerSrc?: string;
  headerHighDpiSrc?: string;
  headerType?: "Image" | "Video";
  publishDate: Date;
  isFeature?: boolean;
  link?: Link;
  labels?: Label[];
}

const props = withDefaults(defineProps<Props>(), {
  headerSrc: defaultImageUrl,
  headerHighDpiSrc: defaultHighDpiImageUrl,
  headerType: "Image",
  isNew: false,
  isFeature: false,
  link: undefined,
  labels: undefined
});

const emit = defineEmits<{
  (event: "open"): void;
}>();

const { t } = useI18n();

const isNew = computed<boolean>(() => {
  const thresholdDays = 14;
  const differenceInMs = new Date().getTime() - props.publishDate.getTime();
  const differenceInDays = differenceInMs / (1000 * 3600 * 24);

  return differenceInDays <= thresholdDays;
});

const isModalOpen = ref(false);

const srcset = computed<string>(() => `${props.headerSrc} 1x, ${props.headerHighDpiSrc} 2x`);

const openModal = () => {
  isModalOpen.value = true;
  emit("open");
};

const closeModal = () => {
  isModalOpen.value = false;
};
</script>

<template>
  <div class="le-card">
    <div class="le-card__label-container">
      <LeLabel v-if="isNew" class="le-card__label">{{ t("new") }}</LeLabel>
      <LeLabel v-if="isFeature" color="attention" class="le-card__label">{{ t("feature") }}</LeLabel>
      <template v-if="labels">
        <LeLabel v-for="label in labels" :key="label.labelText" :color="label.labelColor" class="le-card__label">
          {{ label.labelText }}
        </LeLabel>
      </template>
    </div>
    <div class="le-card__image-container">
      <template v-if="props.headerType === 'Video'">
        <video :src="props.headerSrc" playsinline muted autoplay loop disableRemotePlayback disablePictureInPicture />
      </template>
      <template v-if="props.headerType === 'Image'">
        <img :srcset="srcset" :src="props.headerSrc" />
      </template>
    </div>
    <div class="le-card__content">
      <LeHeading class="le-card__content-heading" size="s" tag="h3">{{ props.headline }}</LeHeading>
      <div class="le-card__content-body">
        <slot />
      </div>
      <div class="le-card__button-container">
        <template v-if="props.link">
          <a :href="props.link.link">
            <LeButton size="small" variant="outline" color="neutral">{{ props.link.linkText }}</LeButton>
          </a>
        </template>
        <template v-else>
          <LeButton size="small" variant="outline" color="neutral" aria-haspopup="dialog" @click="openModal()">
            {{ t("read-more") }}
          </LeButton>
        </template>
      </div>
    </div>
    <slot name="modal" :is-modal-open="isModalOpen" :is-new="isNew" :close-modal="closeModal" @close="closeModal()" />
  </div>
</template>

<style scoped lang="less">
@import "@dsk/styles/all.less";

.le-card {
  position: relative;
  max-width: 100%;
  height: 420px;
  overflow: hidden;
  transition: box-shadow 75ms ease-out;
  border-radius: 4px;
  background: @colors[@neutrals][100];
  box-shadow: @shadows[dropdown];

  &:hover,
  &:focus-within {
    box-shadow: @shadows[floating-hover];
  }

  &__label {
    margin-right: 4px;
    padding: 2px 10px;
    font-weight: 600;
    text-transform: uppercase;
  }

  &__label-container {
    display: flex;
    position: absolute;
    width: 100%;
    padding: 24px 0 0 24px;
  }

  &__image-container {
    display: flex;
    align-content: center;
    justify-content: center;
    width: 100%;
    height: 203px;
    background-color: @colors[@secondary][100];
  }

  &__content {
    display: grid;
    grid-template-rows: auto 1fr auto;
    height: 216px;
    padding: 0 24px;

    &-heading {
      margin-top: 12px;
    }

    &-body {
      margin-bottom: 8px;
      overflow: hidden;
      mask-image: linear-gradient(to bottom, black calc(100% - 20px), transparent 100%);
      .body-text("m");
      color: @colors[@neutrals][400];
    }
  }

  &__button-container {
    align-self: end;
    margin-bottom: 24px;
  }
}

p {
  margin: 0;
}
</style>

<i18n lang="json">
{
  "da": {
    "new": "Nyt",
    "read-more": "Læs mere",
    "feature": "Opdatering"
  },
  "en": {
    "new": "New",
    "read-more": "Read more",
    "feature": "Update"
  },
  "de": {
    "new": "Neu",
    "read-more": "Weiterlesen",
    "feature": "update"
  }
}
</i18n>
